import { Alert, Slider } from 'antd';
import { useEffect, useState } from 'react';
import Plot from 'react-plotly.js';
import { useAppSelector } from '../../../app/hooks';
import { defaultColorPalette } from '../../../compute/colormap';
import { checkAllRecordsExcluded, closeArray, defaultPlotlyArguments, getRecordNameWithBrB, getRecordNameShort } from '../../../compute/utils';
import {
  selectAggregatePeptides,
  selectBoundariesMap,
  selectChemicalCalibrationItemNames,
  selectColormap,
  selectColormapRef,
  selectExcludedPeptides,
  selectExcludedRecordIDs,
  selectExcludedSpots,
  selectHumidityCompensationCalibrantName,
  selectHumidityCompensationPositionOffset,
  selectHumidityCompensationSubstractionGain,
  selectRecords,
  selectSessionID,
  selectSubtractItemName,
} from '../../../features/analysisConfig/analysisConfigSlice';
import { AryRecord, NormType, Signature, MultiSignaturesFigureType, ComparisonMeasuresType } from '../../../types/analysisTypes';
import { fetchAuthorizedAPIEndpoint, useAuth0OrQueryAuth } from '../../../utils';

export const MultiSignaturesFigure: React.FC<{
  figureType: MultiSignaturesFigureType;
  normType: NormType;
  heatmapRangeValue: number[];
  setHeatmapRangeValue: React.Dispatch<React.SetStateAction<number[]>>;
  qualityControlMode?: boolean;
}> = (props) => {
  const { figureType, normType, heatmapRangeValue, setHeatmapRangeValue, qualityControlMode } = props;

  const accessToken = useAuth0OrQueryAuth();

  const sessionID = useAppSelector(selectSessionID);
  const boundariesMap = useAppSelector(selectBoundariesMap);
  const excludedRecordIDs = useAppSelector(selectExcludedRecordIDs);
  const records = useAppSelector(selectRecords);
  const cmap = useAppSelector(selectColormap);
  const cmapRef = useAppSelector(selectColormapRef);
  const subtractItemName = useAppSelector(selectSubtractItemName);
  const aggregatePeptides = useAppSelector(selectAggregatePeptides);
  const excludedSpots = useAppSelector(selectExcludedSpots);
  const excludedPeptides = useAppSelector(selectExcludedPeptides);

  const humidityCalibrationCalibrantName = useAppSelector(selectHumidityCompensationCalibrantName);
  const humidityCalibrationPositionOffset = useAppSelector(selectHumidityCompensationPositionOffset);
  const humidityCalibrationSubstractionGain = useAppSelector(selectHumidityCompensationSubstractionGain);

  const chemicalCalibrationItemNames = useAppSelector(selectChemicalCalibrationItemNames);

  const [signatures, setSignatures] = useState<Signature[]>([]);
  const [error, setError] = useState<string | undefined>();
  const [_cmap, setCmap] = useState<Record<string, string> | undefined>();

  useEffect(() => {
    if (!accessToken) return;
    const { error } = checkAllRecordsExcluded(records, excludedRecordIDs);
    if (error) {
      setError(error);
      return;
    } else setError(undefined);

    fetchAuthorizedAPIEndpoint(`/compute/signatures?session_id=${sessionID}`, accessToken, {
      method: 'POST',
      body: JSON.stringify({
        sessionID,
        boundariesMap,
        excludedRecordIDs,
        aggregatePeptides,
        subtractItemName,
        excludedPeptides,
        excludedSpots,
        humidityCompensation: {
          calibrantName: humidityCalibrationCalibrantName,
          positionOffset: humidityCalibrationPositionOffset,
          SubstractionGain: humidityCalibrationSubstractionGain,
        },
        chemicalCalibrationItemNames,
      }),
    })
      .then((resp) => {
        if (resp.ok) {
          return resp.json();
        } else {
          throw resp.json();
        }
      })
      .then((signatures: Signature[]) => {
        for (let i = 0; i < signatures.length; i++) {
          const sig = signatures[i];
          if (!sig.NormalizedSignature) {
            setError('null signature received');
            return;
          }
        }
        setError('');
        setSignatures(signatures);
      })
      .catch((e) => {
        Promise.resolve(e).then((resp: { Reason: string }) => {
          setError(resp.Reason);
        });
      });
  }, [
    accessToken,
    sessionID,
    boundariesMap,
    excludedRecordIDs,
    aggregatePeptides,
    subtractItemName,
    excludedSpots,
    excludedPeptides,
    humidityCalibrationCalibrantName,
    humidityCalibrationPositionOffset,
    humidityCalibrationSubstractionGain,
    chemicalCalibrationItemNames,
  ]);

  useEffect(() => {
    if (qualityControlMode && cmapRef !== undefined) {
      setCmap({ ...cmapRef[ComparisonMeasuresType.Reference], ...cmapRef[ComparisonMeasuresType.Test], ...cmapRef[ComparisonMeasuresType.Other] });
    } else if (cmap !== undefined) {
      setCmap(cmap);
    }
  }, [cmap, cmapRef, qualityControlMode]);

  if (error !== '') {
    return (
      <div>
        <Alert type="error" message={error} />
      </div>
    );
  }

  if (signatures === undefined || signatures === null || signatures.length === 0 || _cmap === undefined || records === undefined) {
    return null;
  }

  var plotlyLayout: Partial<Plotly.Layout> = { ...defaultPlotlyArguments.layout };

  var plotlyData: Plotly.Data[] = [];
  switch (figureType) {
    case +MultiSignaturesFigureType.Radar:
      let globalMin: number = 1e6;
      let globalMax: number = -1e6;

      var uniqueItemNames: Set<string> = new Set();

      signatures.forEach((signature, i) => {
        var record: AryRecord | undefined = undefined;
        for (let i = 0; i < records.length; i++) {
          if (records[i].ID === signature.RecordID) {
            record = records[i];
            break;
          }
        }
        if (record === undefined) {
          return;
        }
        if (record.ItemName === humidityCalibrationCalibrantName && normType === NormType.Normalized) {
          return;
        }
        let label = getRecordNameShort(record);
        var signatureVector: number[] = signature.RawSignature;
        switch (normType) {
          case +NormType.Normalized:
            signatureVector = signature.NormalizedSignature.map((e) => e * 100);
            break;
        }

        globalMin = Math.min(globalMin, Math.min(...signatureVector));
        globalMax = Math.max(globalMax, Math.max(...signatureVector));

        plotlyData.push({
          type: 'scatterpolar',
          hoverlabel: { namelength: -1 },
          name: record.ItemName,
          theta: closeArray(signature.Peptides.map((p) => '&nbsp;' + p)),
          r: closeArray(signatureVector),
          hoverinfo: 'y+x',
          text: label,
          hoveron: 'points',
          legendgroup: record.ItemName,
          showlegend: !uniqueItemNames.has(record.ItemName),
          line: {
            color: qualityControlMode ? _cmap[record.ItemName] : defaultColorPalette[_cmap[record.ItemName]],
          },
        });

        uniqueItemNames.add(record.ItemName);
      });

      if (normType === +NormType.Normalized) {
        const range = [globalMin, globalMax];
        if (plotlyLayout.polar !== undefined) {
          if (plotlyLayout.polar.radialaxis !== undefined) {
            plotlyLayout.polar.radialaxis.range = range;
          } else {
            plotlyLayout.polar.radialaxis = {
              range: range,
            };
          }
        } else {
          plotlyLayout.polar = {
            radialaxis: {
              range: range,
            },
          };
        }
      } else {
        plotlyLayout.polar = { radialaxis: { fixedrange: true } };
      }
      break;
    case +MultiSignaturesFigureType.Heatmap:
      var signaturesArray: number[][] = [];
      var labels: string[] = [];
      var colors: string[] = [];
      var peptides: string[] = [];

      var ticktextY: string[] = [];
      var tickvalsY: number[] = [];

      var ticktextX: string[] = [];
      var tickvalsX: number[] = [];

      var _min = 1e6;
      var _max = -1e6;

      var signaturesWithRecords: { signature: Signature; record: AryRecord }[] = [];

      signatures.forEach((signature) => {
        var record: AryRecord | undefined = undefined;
        for (let i = 0; i < records.length; i++) {
          if (records[i].ID === signature.RecordID) {
            record = records[i];
            break;
          }
        }
        if (record === undefined) return;
        signaturesWithRecords.push({ signature, record });
      });

      signaturesWithRecords.sort((a, b) => {
        let sorterA = `${a.record.ItemName} #${a.record.CycleNumber.toString().padStart(3)}`;
        let sorterB = `${b.record.ItemName} #${b.record.CycleNumber.toString().padStart(3)}`;
        return sorterA < sorterB ? -1 : 1;
      });
      signaturesWithRecords.map(({ signature, record }, i) => {
        let label = getRecordNameWithBrB(record);
        var signatureVector: number[] = signature.RawSignature;
        switch (normType) {
          case +NormType.Normalized:
            signatureVector = signature.NormalizedSignature.map((e) => e * 100);
            break;
        }
        if (peptides.length === 0) {
          peptides = signature.Peptides.map((p) => '&nbsp;' + p);
          peptides.forEach((p, i) => {
            let parts = p.split('[');
            if (parts.length === 2) {
              let peptideCode = parts[0];
              if (ticktextX.length === 0 || peptideCode !== ticktextX[ticktextX.length - 1]) {
                ticktextX.push(peptideCode);
                tickvalsX.push(i);
              }
            } else {
              ticktextX.push(p);
              tickvalsX.push(i);
            }
          });
          if (ticktextX.length === 0 || record.ItemName !== ticktextX[ticktextX.length - 1]) {
          }
        }
        if (ticktextY.length === 0 || record.ItemName !== ticktextY[ticktextY.length - 1]) {
          ticktextY.push(record.ItemName);
          tickvalsY.push(i);
        }
        colors.push(qualityControlMode ? _cmap[record.ItemName] : defaultColorPalette[_cmap[record.ItemName]]);
        labels.push(label);
        signaturesArray.push(signatureVector);
        _min = Math.min(_min, ...signatureVector);
        _max = Math.max(_max, ...signatureVector);
      });
      const _range = _max - _min;
      const zmin = _min + heatmapRangeValue[0] * _range;
      const zmax = _max - (1 - heatmapRangeValue[1]) * _range;

      plotlyData.push({
        type: 'heatmap',
        x: peptides,
        y: labels,
        z: signaturesArray,
        hoverinfo: 'y+x+z',
        hoveron: 'points',
        showscale: false,
        xgap: 0.5,
        ygap: 0.5,
        zmin: zmin,
        zmax: zmax,
        colorscale: 'Viridis', //as Plotly.ColorScale
      });
      plotlyLayout.yaxis = {
        tickmode: 'array',
        ticktext: ticktextY,
        tickvals: tickvalsY,
        automargin: true,
      };
      plotlyLayout.xaxis = {
        tickmode: 'array',
        ticktext: ticktextX,
        tickvals: tickvalsX,
        automargin: true,
      };
      break;
  }

  const plotlyConfig = { ...defaultPlotlyArguments.config };

  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'end', alignItems: 'center', flexDirection: 'row' }}>
      <Plot divId="signatures_scatterpolar_plot" debug={true} data={plotlyData} useResizeHandler={true} layout={plotlyLayout} config={plotlyConfig} style={defaultPlotlyArguments.style} />
      {figureType === +MultiSignaturesFigureType.Heatmap ? (
        <Slider
          style={{ height: '70%' }}
          min={0}
          max={1}
          range
          value={heatmapRangeValue}
          step={0.01}
          vertical
          onChange={(value) => {
            setHeatmapRangeValue(value);
          }}
        />
      ) : null}
    </div>
  );
};
